import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Table,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import axiosInstance from '../../api/Axios';

const ManageFunders = () => {
  const [funders, setFunders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [selectedFunder, setSelectedFunder] = useState(null);
  const [activeTab, setActiveTab] = useState('identification');

  useEffect(() => {
    fetchFunders();
  }, []);

  const fetchFunders = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axiosInstance.get('/api/accounts/funders/management/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      setFunders(response.data);
    } catch (error) {
      console.error('Error fetching funders:', error);
      setError('Failed to fetch funders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewFunder = (funder) => {
    setSelectedFunder(funder);
    setActiveTab('identification'); // Reset to first tab
    setViewModal(true);
  };

  const closeViewModal = () => {
    setViewModal(false);
    setSelectedFunder(null);
    setActiveTab('identification'); // Reset tab when closing
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  return (
    <div style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <CardTitle tag="h5" className="mb-0">
              Manage Funders
            </CardTitle>
          </div>

          {error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner size="lg" color="primary" />
              <div className="mt-2">Loading funders...</div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Funder Code</th>
                    <th>Funder Name</th>
                    <th>Country</th>
                    <th>Primary Representative</th>
                    <th>Secondary Representative</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {funders.map((funder) => (
                    <tr key={funder.id}>
                      <td>
                        <code className="text-primary">{funder.code || funder.funder_code}</code>
                      </td>
                      <td>
                        <div className="fw-bold">{funder.name}</div>
                        {funder.short_name && (
                          <div className="text-muted small">({funder.short_name})</div>
                        )}
                      </td>
                      <td>
                        <div className="text-muted">{funder.country || 'Not specified'}</div>
                      </td>
                      <td>
                        {funder.primary_representative ? (
                          <div>
                            <div className="fw-bold">
                              {funder.primary_representative.first_name} {funder.primary_representative.last_name}
                            </div>
                            <div className="text-muted small">
                              {funder.primary_representative.email_address}
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted">Not assigned</span>
                        )}
                      </td>
                      <td>
                        {funder.secondary_representative ? (
                          <div>
                            <div className="fw-bold">
                              {funder.secondary_representative.first_name} {funder.secondary_representative.last_name}
                            </div>
                            <div className="text-muted small">
                              {funder.secondary_representative.email_address}
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted">Not assigned</span>
                        )}
                      </td>
                      <td>
                        <UncontrolledDropdown>
                          <DropdownToggle 
                            caret 
                            size="sm" 
                            color="outline-primary"
                          >
                            Action
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem 
                              onClick={() => handleViewFunder(funder)}
                            >
                              <i className="fas fa-eye me-2"></i>
                              View Details
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Funder Details Modal */}
      <Modal isOpen={viewModal} toggle={closeViewModal} size="lg" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
        <ModalHeader toggle={closeViewModal} style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
          Funder Details
        </ModalHeader>
        <ModalBody style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
          {selectedFunder && (
            <div>
              {/* Tab Navigation */}
              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={activeTab === 'identification' ? 'active' : ''}
                    onClick={() => toggleTab('identification')}
                    style={{ cursor: 'pointer' }}
                  >
                    Identification
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab === 'officers' ? 'active' : ''}
                    onClick={() => toggleTab('officers')}
                    style={{ cursor: 'pointer' }}
                  >
                    Funder Officers
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab === 'documents' ? 'active' : ''}
                    onClick={() => toggleTab('documents')}
                    style={{ cursor: 'pointer' }}
                  >
                    Funder Documents
                  </NavLink>
                </NavItem>
              </Nav>

              {/* Tab Content */}
              <TabContent activeTab={activeTab} className="mt-3">
                {/* Identification Tab */}
                <TabPane tabId="identification">
                  <div className="table-responsive">
                    <table className="table table-borderless">
                      <tbody>
                        <tr>
                          <td><strong>Funder Nationality:</strong></td>
                          <td>{selectedFunder.country || 'Not specified'}</td>
                        </tr>
                        <tr>
                          <td><strong>Funder Name:</strong></td>
                          <td>{selectedFunder.name}</td>
                        </tr>
                        <tr>
                          <td><strong>System Funder Code:</strong></td>
                          <td><code className="text-primary">{selectedFunder.code || 'Not assigned'}</code></td>
                        </tr>
                        <tr>
                          <td><strong>Funder Short Name:</strong></td>
                          <td>{selectedFunder.short_name || 'Not specified'}</td>
                        </tr>
                        <tr>
                          <td><strong>BMO Funder Code:</strong></td>
                          <td><code className="text-info">{selectedFunder.funder_code || 'Not assigned'}</code></td>
                        </tr>
                        <tr>
                          <td><strong>Funder Domain Name:</strong></td>
                          <td>
                            {selectedFunder.domain ? `@${selectedFunder.domain}` : 'Not specified'}
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Funder Website:</strong></td>
                          <td>
                            {selectedFunder.domain ? (
                              <a href={`https://www.${selectedFunder.domain}`} target="_blank" rel="noopener noreferrer">
                                www.{selectedFunder.domain}
                              </a>
                            ) : 'Not specified'}
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Reporting Currency:</strong></td>
                          <td>{selectedFunder.default_currency || 'Not specified'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </TabPane>

                {/* Funder Officers Tab */}
                <TabPane tabId="officers">
                  <div>
                    <h6 className="text-primary mb-3">All Accounts Linked to this Funder</h6>
                    
                    {/* Primary Representative */}
                    {selectedFunder.primary_representative && (
                      <div className="mb-4 p-3 border rounded">
                        <h6 className="text-success">
                          <i className="fas fa-user-tie me-2"></i>
                          Primary Representative
                        </h6>
                        <div className="row">
                          <div className="col-12">
                            <div className="mb-2"><strong>First Name:</strong> {selectedFunder.primary_representative.first_name || 'Not provided'}</div>
                            <div className="mb-2"><strong>Surname:</strong> {selectedFunder.primary_representative.last_name || 'Not provided'}</div>
                            <div className="mb-2"><strong>Title:</strong> {selectedFunder.primary_representative.title || 'Not provided'}</div>
                            <div className="mb-2"><strong>Email Address:</strong> {selectedFunder.primary_representative.email_address || 'Not provided'}</div>
                            <div className="mb-2"><strong>Office Phone Number:</strong> {selectedFunder.primary_representative.office_phone_number || selectedFunder.primary_representative.phone_number || 'Not provided'}</div>
                            <div className="mb-2"><strong>Mobile Number:</strong> {selectedFunder.primary_representative.mobile_number || 'Not provided'}</div>
                            <div className="mb-2"><strong>Job Title:</strong> {selectedFunder.primary_representative.job_title || selectedFunder.primary_representative.title || 'Not provided'}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Secondary Representative */}
                    {selectedFunder.secondary_representative && (
                      <div className="mb-4 p-3 border rounded">
                        <h6 className="text-info">
                          <i className="fas fa-user me-2"></i>
                          Secondary Representative
                        </h6>
                        <div className="row">
                          <div className="col-12">
                            <div className="mb-2"><strong>First Name:</strong> {selectedFunder.secondary_representative.first_name || 'Not provided'}</div>
                            <div className="mb-2"><strong>Surname:</strong> {selectedFunder.secondary_representative.last_name || 'Not provided'}</div>
                            <div className="mb-2"><strong>Title:</strong> {selectedFunder.secondary_representative.title || 'Not provided'}</div>
                            <div className="mb-2"><strong>Email Address:</strong> {selectedFunder.secondary_representative.email_address || 'Not provided'}</div>
                            <div className="mb-2"><strong>Office Phone Number:</strong> {selectedFunder.secondary_representative.office_phone_number || selectedFunder.secondary_representative.phone_number || 'Not provided'}</div>
                            <div className="mb-2"><strong>Mobile Number:</strong> {selectedFunder.secondary_representative.mobile_number || 'Not provided'}</div>
                            <div className="mb-2"><strong>Job Title:</strong> {selectedFunder.secondary_representative.job_title || selectedFunder.secondary_representative.title || 'Not provided'}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Officers */}
                    {selectedFunder.officers && selectedFunder.officers.length > 0 ? (
                      <div>
                        <h6 className="text-warning mb-3">
                          <i className="fas fa-users me-2"></i>
                          Funder Officers ({selectedFunder.officers.length})
                        </h6>
                        {selectedFunder.officers.map((officer, index) => (
                          <div key={index} className="mb-3 p-3 border rounded">
                            <div className="row">
                              <div className="col-12">
                                <div className="mb-2"><strong>First Name:</strong> {officer.first_name || 'Not provided'}</div>
                                <div className="mb-2"><strong>Surname:</strong> {officer.last_name || 'Not provided'}</div>
                                <div className="mb-2"><strong>Title:</strong> {officer.title || 'Not provided'}</div>
                                <div className="mb-2"><strong>Email Address:</strong> {officer.email_address || 'Not provided'}</div>
                                <div className="mb-2"><strong>Office Phone Number:</strong> {officer.office_phone_number || officer.phone_number || 'Not provided'}</div>
                                <div className="mb-2"><strong>Mobile Number:</strong> {officer.mobile_number || 'Not provided'}</div>
                                <div className="mb-2"><strong>Job Title:</strong> {officer.job_title || officer.title || 'Not provided'}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted">
                        <i className="fas fa-info-circle me-2"></i>
                        No officers assigned to this funder.
                      </div>
                    )}

                    {/* Summary */}
                    <div className="mt-4 p-3 bg-light rounded">
                      <strong>Total Accounts: </strong>
                      {(selectedFunder.primary_representative ? 1 : 0) + 
                       (selectedFunder.secondary_representative ? 1 : 0) + 
                       (selectedFunder.officers ? selectedFunder.officers.length : 0)}
                    </div>
                  </div>
                </TabPane>

                {/* Funder Documents Tab */}
                <TabPane tabId="documents">
                  <div className="text-center py-5">
                    <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">Funder Documents</h5>
                    <p className="text-muted">Document management functionality will be implemented here.</p>
                  </div>
                </TabPane>
              </TabContent>
            </div>
          )}
        </ModalBody>
        <ModalFooter style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
          <Button color="secondary" onClick={closeViewModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ManageFunders;